import { makeStyles } from "@material-ui/core";
import React from "react";

const useStyles = makeStyles(() => ({
  "@keyframes cardEnter": {
    from: { opacity: 0, transform: "translateY(24px) scale(0.985)" },
    to: { opacity: 1, transform: "translateY(0) scale(1)" },
  },
  "@keyframes barGrow": {
    from: { transform: "scaleX(0)" },
    to: { transform: "scaleX(1)" },
  },
  root: {
    "&[data-result-reveal='pending']": { opacity: 0, transform: "translateY(24px) scale(0.985)" },
    "&[data-result-reveal='revealed']": {
      animation: "$cardEnter 680ms cubic-bezier(0.16, 1, 0.3, 1) both",
      "& [data-result-bar]": {
        transformOrigin: "left center",
        animation: "$barGrow 900ms cubic-bezier(0.22, 1, 0.36, 1) 180ms both",
      },
    },
    // Tab で先に移動した場合は、フォーカス先をすぐに見せる。
    "&:focus-within, &:focus-within [data-result-bar]": { animation: "none", opacity: 1, transform: "none" },
    "@media (prefers-reduced-motion: reduce), print": {
      "&&, && [data-result-bar]": { animation: "none", opacity: 1, transform: "none" },
    },
  },
}));

/** 各カードを初めて画面に入った時だけ表示。Observer が無い環境では最初から読める。 */
export const ResultReveal: React.FC<{ className: string; delay?: number }> = ({ children, className, delay = 0 }) => {
  const classes = useStyles();
  const ref = React.useRef<HTMLDivElement>(null);
  const observer = React.useRef<IntersectionObserver>();
  const [phase, setPhase] = React.useState("static");

  React.useLayoutEffect(() => {
    const element = ref.current;
    if (
      !element ||
      typeof IntersectionObserver === "undefined" ||
      (typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches)
    ) {
      return;
    }

    let active = true;
    const instance = new IntersectionObserver(
      (entries) => {
        if (!active || observer.current !== instance || !entries.some((entry) => entry.isIntersecting)) return;
        active = false;
        setPhase("revealed");
        instance.disconnect();
        observer.current = undefined;
      },
      { rootMargin: "0px 0px -32px 0px", threshold: 0 }
    );
    observer.current = instance;
    setPhase("pending");
    instance.observe(element);
    return () => {
      active = false;
      instance.disconnect();
      observer.current = undefined;
    };
  }, []);

  const handleFocus = () => {
    observer.current?.disconnect();
    observer.current = undefined;
    setPhase("static");
  };

  return (
    <div
      ref={ref}
      className={`${className} ${classes.root}`}
      data-result-reveal={phase}
      style={{ animationDelay: `${delay}ms` }}
      onFocusCapture={handleFocus}
    >
      {children}
    </div>
  );
};
